/**
 * WSAdminInquiry - 워크스페이스 문의하기 페이지
 * 
 * 사용자가 운영자에게 직접 문의하고 이력을 확인할 수 있는 페이지
 */

import React, { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { MessageCircle, Clock, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { Workspace } from '../../../models/Workspace';
import { Inquiry, InquiryType, InquiryMessage, INQUIRY_TYPE_LABELS, INQUIRY_STATUS_LABELS, defaultInquiry } from '../../../models/Inquiry';
import { Fire } from '../../../services/FireClient';
import { My } from '../../../services/My';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../../components/ui/dialog";
import { IconPlus } from '../../components/FigmaIcons';

interface WSAdminInquiryProps {
    workspace: Workspace;
}

const WSAdminInquiry: React.FC<WSAdminInquiryProps> = ({ workspace }) => {
    const { i18n } = useLingui();
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const { toast } = useToast();

    // 새 문의 폼
    const [newInquiry, setNewInquiry] = useState<Partial<Inquiry>>({
        type: 'support',
        subject: '',
        message: '',
    });

    useEffect(() => {
        loadInquiries();
    }, [workspace.id]);

    const loadInquiries = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(Fire.store, `workspaces/${workspace.id}/inquiries`),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            const list = snap.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                    updatedAt: data.updatedAt?.toDate?.() || new Date(),
                } as Inquiry;
            });
            setInquiries(list);
        } catch (error) {
            console.error('Failed to load inquiries:', error);
            toast({ title: t`Failed to load inquiries`, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!newInquiry.subject?.trim() || !newInquiry.message?.trim()) {
            toast({ title: t`Please fill in all fields`, variant: 'destructive' });
            return;
        }

        setSubmitting(true);
        try {
            const snapshot = {
                tier: workspace.tier || 'FREE',
                memberCount: workspace.overview?.usage?.memberCount || 0,
                storageBytes: workspace.overview?.usage?.storageBytes || 0,
                channelCount: workspace.overview?.usage?.channelCount || 0,
            };

            const inquiryData = {
                ...defaultInquiry(workspace.id, workspace.name),
                ...newInquiry,
                snapshot,
                createdBy: My.user.id,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            await addDoc(collection(Fire.store, `workspaces/${workspace.id}/inquiries`), inquiryData);

            toast({ title: t`Inquiry submitted successfully` });
            setShowNewDialog(false);
            setNewInquiry({ type: 'support', subject: '', message: '' });
            loadInquiries();
        } catch (error) {
            console.error('Failed to submit inquiry:', error);
            toast({ title: t`Failed to submit inquiry`, variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    // 고객 답장 전송
    const handleSendReply = async () => {
        if (!selectedInquiry || !replyMessage.trim()) return;
        setSubmitting(true);
        try {
            const message: InquiryMessage = {
                id: `msg_${Date.now()}`,
                content: replyMessage.trim(),
                authorId: My.user.id,
                authorName: My.user.name || 'ユーザー',
                authorRole: 'customer',
                createdAt: new Date(),
            };

            const ref = doc(Fire.store, `workspaces/${workspace.id}/inquiries`, selectedInquiry.id);
            await updateDoc(ref, {
                messages: arrayUnion(message),
                updatedAt: serverTimestamp(),
            });

            // 로컬 상태 업데이트
            setSelectedInquiry({
                ...selectedInquiry,
                messages: [...(selectedInquiry.messages || []), message],
            });
            setReplyMessage('');
            loadInquiries();
            toast({ title: t`Reply sent` });
        } catch (error) {
            console.error('Failed to send reply:', error);
            toast({ title: t`Failed to send reply`, variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'new':
                return <AlertCircle className="w-4 h-4 text-blue-500" />;
            case 'in_progress':
                return <Clock className="w-4 h-4 text-amber-500" />;
            case 'resolved':
                return <CheckCircle2 className="w-4 h-4 text-green-500" />;
            default:
                return null;
        }
    };

    const getStatusBadge = (status: string) => {
        const colors: Record<string, string> = {
            new: 'bg-blue-100 text-blue-700',
            in_progress: 'bg-amber-100 text-amber-700',
            resolved: 'bg-green-100 text-green-700',
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    return (
        <div className="w-full p-4 md:p-6 lg:p-8 space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-muted border border-border">
                        <MessageCircle className="h-6 w-6 text-card-foreground" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight"><Trans>Inquiries</Trans></h2>
                        <p className="text-sm text-muted-foreground"><Trans>Contact support or ask questions</Trans></p>
                    </div>
                </div>
                <Button onClick={() => setShowNewDialog(true)} className="w-full sm:w-auto">
                    <IconPlus className="w-4 h-4 mr-2" />
                    <Trans>New Inquiry</Trans>
                </Button>
            </div>

            {/* Inquiry List */}
            {loading ? (
                <div className="text-center py-12 text-muted-foreground">
                    <Trans>Loading...</Trans>
                </div>
            ) : inquiries.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="py-12 text-center">
                        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-lg font-medium text-muted-foreground"><Trans>No inquiries yet</Trans></p>
                        <p className="text-sm text-muted-foreground mt-2">
                            <Trans>Click "New Inquiry" to contact support</Trans>
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {inquiries.map(inquiry => (
                        <Card key={inquiry.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                                <div className="flex flex-col gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            {getStatusIcon(inquiry.status)}
                                            <h3 className="font-semibold truncate">{inquiry.subject}</h3>
                                        </div>
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                            {inquiry.message}
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant="outline" className="text-xs">
                                                {i18n._(INQUIRY_TYPE_LABELS[inquiry.type] || inquiry.type)}
                                            </Badge>
                                            <Badge className={`text-xs ${getStatusBadge(inquiry.status)}`}>
                                                {i18n._(INQUIRY_STATUS_LABELS[inquiry.status as keyof typeof INQUIRY_STATUS_LABELS] || inquiry.status)}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(inquiry.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedInquiry(inquiry);
                                            setReplyMessage('');
                                        }}
                                        className="flex items-center justify-center gap-2 bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700 w-full sm:w-auto"
                                    >
                                        <MessageCircle className="w-4 h-4" />
                                        {inquiry.messages && inquiry.messages.length > 0 ? (
                                            <>
                                                <span className="font-semibold">{inquiry.messages.length}件</span>
                                                <span className="text-xs opacity-75">
                                                    ({(() => {
                                                        const d = inquiry.messages[inquiry.messages.length - 1].createdAt as any;
                                                        const date = d?.toDate ? d.toDate() : new Date(d);
                                                        return date.toLocaleDateString();
                                                    })()})
                                                </span>
                                            </>
                                        ) : (
                                            <span>対話を開く</span>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Inquiry Detail Dialog */}
            <Dialog open={!!selectedInquiry} onOpenChange={(open) => !open && setSelectedInquiry(null)}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedInquiry?.subject}</DialogTitle>
                        <DialogDescription>
                            {selectedInquiry && new Date(selectedInquiry.createdAt).toLocaleString()}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedInquiry && (
                        <div className="space-y-4">
                            {/* 원본 문의 */}
                            <div className="p-3 bg-muted/50 rounded-lg">
                                <div className="text-sm text-muted-foreground mb-1"><Trans>Original Message</Trans></div>
                                <div className="text-foreground whitespace-pre-wrap">{selectedInquiry.message}</div>
                            </div>

                            {/* 쓰레드 대화 */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-card-foreground">
                                    <Trans>Conversation</Trans>
                                </label>
                                <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-muted/50 rounded-lg">
                                    {(!selectedInquiry.messages || selectedInquiry.messages.length === 0) ? (
                                        <div className="text-sm text-muted-foreground text-center py-2">
                                            <Trans>No replies yet</Trans>
                                        </div>
                                    ) : (
                                        selectedInquiry.messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={
                                                    msg.authorRole === 'admin'
                                                        ? "p-2 rounded-lg text-sm bg-indigo-100 text-indigo-900 ml-4"
                                                        : "p-2 rounded-lg text-sm bg-background border mr-4"
                                                }
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-xs font-medium ${msg.authorRole === 'admin' ? "text-indigo-600" : "text-card-foreground"}`}>
                                                        {msg.authorName}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {(() => {
                                                            const d = msg.createdAt as any;
                                                            const date = d?.toDate ? d.toDate() : new Date(d);
                                                            return date.toLocaleString();
                                                        })()}
                                                    </span>
                                                </div>
                                                <div className="whitespace-pre-wrap">{msg.content}</div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* 답장 입력 (완료 상태가 아닐 때만) */}
                                {selectedInquiry.status !== 'resolved' && (
                                    <div className="flex gap-2">
                                        <Textarea
                                            value={replyMessage}
                                            onChange={(e) => setReplyMessage(e.target.value)}
                                            placeholder={t`Write your reply...`}
                                            rows={2}
                                            className="flex-1"
                                        />
                                        <Button
                                            onClick={handleSendReply}
                                            disabled={submitting || !replyMessage.trim()}
                                            size="sm"
                                            className="self-end"
                                        >
                                            <Send className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedInquiry(null)}>
                            <Trans>Close</Trans>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Inquiry Dialog */}
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle><Trans>New Inquiry</Trans></DialogTitle>
                        <DialogDescription>
                            <Trans>Submit a question or request to our support team</Trans>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label><Trans>Type</Trans></Label>
                            <Select
                                value={newInquiry.type}
                                onValueChange={(val) => setNewInquiry({ ...newInquiry, type: val as InquiryType })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(INQUIRY_TYPE_LABELS).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{i18n._(label)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label><Trans>Subject</Trans></Label>
                            <Input
                                value={newInquiry.subject}
                                onChange={(e) => setNewInquiry({ ...newInquiry, subject: e.target.value })}
                                placeholder={t`Brief description of your inquiry`}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label><Trans>Message</Trans></Label>
                            <Textarea
                                value={newInquiry.message}
                                onChange={(e) => setNewInquiry({ ...newInquiry, message: e.target.value })}
                                placeholder={t`Detailed description of your question or request`}
                                className="min-h-[150px]"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNewDialog(false)}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting ? (
                                <Trans>Submitting...</Trans>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    <Trans>Submit</Trans>
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WSAdminInquiry;
